import { LoadType } from './load-type';
import { worldResource } from '@/world';
import type { ResourceInfo } from './pe-reader';

interface PendingPromise<T> {
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
  promise: Promise<T>;
}

function createPendingPromise<T>(timeoutMs = 30000, onTimeout?: (error: Error) => void): PendingPromise<T> {
  let resolve!: (value: T) => void;
  let reject!: (reason: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // Attach a rejection observer immediately: concurrent requests may still be
  // fetching while another waiter observes a worker/network failure.
  void promise.catch(() => {});
  const timeout=timeoutMs > 0 ? setTimeout(()=>{const error=new Error('Graphics decoding timed out');reject(error);onTimeout?.(error);},timeoutMs) : undefined;
  return { resolve:value=>{clearTimeout(timeout);resolve(value);}, reject:error=>{clearTimeout(timeout);reject(error);}, promise };
}

const LRU_MAX_SIZE = 500;

export class GfxLoader {
  private worker!: Worker;
  private activeReadBatches = 0;
  private bitmapRequests = new Map<string, Promise<ImageBitmap>>();

  // Atlas/compositor callers retain borrowed bitmaps and raw pixels until their
  // entire batch has been copied. An LRU eviction must not close those images.
  retainBatch(): () => void {
    this.activeReadBatches++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.activeReadBatches--;
      this.trimCache();
    };
  }

  private trimCache() {
    if (this.activeReadBatches) return;
    while (this.bitmapCache.size > LRU_MAX_SIZE) {
      const firstKey = this.bitmapCache.keys().next().value as string;
      this.bitmapCache.get(firstKey)?.close();
      this.bitmapCache.delete(firstKey);
      this.pixelCache.delete(firstKey);
    }
  }

  private workerFailed=false;
  private workerGeneration = 0;
  private downloads = new Map<number, AbortController>();
  private egfs: Map<number, Map<number, ResourceInfo>> = new Map();
  private pendingEGFs: Map<number, PendingPromise<Map<number, ResourceInfo>>> =
    new Map();
  private pendingResources: Map<
    string,
    PendingPromise<{ pixels: ArrayBuffer; width: number; height: number }>
  > = new Map();

  // LRU in-memory cache: key = "fileID:resourceID"
  private bitmapCache: Map<string, ImageBitmap> = new Map();
  // Raw pixel cache for compositor worker (same LRU key space)
  private pixelCache: Map<
    string,
    { pixels: Uint8ClampedArray; width: number; height: number }
  > = new Map();

  private batchPromises: Map<number, PendingPromise<void>> = new Map();
  private batchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() { this.startWorker(); }

  private startWorker() {
    this.workerFailed=false;
    const generation = ++this.workerGeneration;
    this.worker = new Worker(
      new URL('./gfx-loader.worker.ts', import.meta.url),
      { type: 'module' },
    );

    this.worker.onmessage = (event: MessageEvent) => {
      if (generation !== this.workerGeneration) return;
      const data = event.data;
      switch (data.loadType) {
        case LoadType.DIB:
          this.handleDIBMessage(data);
          break;
        case LoadType.EGF:
          this.handleEGFMessage(data);
          break;
        default:
          console.error(`Unhandled LoadType: ${data.loadType}`);
      }
    };

    this.worker.onerror = (error) => {
      if (generation !== this.workerGeneration) return;
      console.error('GFX Worker error:', error);
      this.failWorker(error);
    };
  }

  private failWorker(error: unknown) {
    this.workerFailed = true;
    this.workerGeneration++;
    this.worker.terminate();
    for (const controller of this.downloads.values()) controller.abort();
    this.downloads.clear();
    for (const request of this.pendingEGFs.values()) request.reject(error);
    for (const request of this.pendingResources.values()) request.reject(error);
    this.pendingEGFs.clear();
    this.pendingResources.clear();
    // The replacement worker has no archives. Keeping this directory cache
    // makes every later DIB request fail, even after returning to login.
    this.egfs.clear();
  }

  private ensureWorker() {
    if(this.workerFailed){this.worker.terminate();this.startWorker();}
  }

  private handleDIBMessage(data: {
    fileID: number;
    resourceID: number;
    pixels?: ArrayBuffer;
    width?: number;
    height?: number;
    error?: string;
  }) {
    const key = `${data.fileID}:${data.resourceID}`;
    const pending = this.pendingResources.get(key);
    if (pending) {
      this.pendingResources.delete(key);
      if (data.error) {
        pending.reject(data.error);
      } else {
        pending.resolve({
          pixels: data.pixels!,
          width: data.width!,
          height: data.height!,
        });
      }
    }
  }

  private handleEGFMessage(data: {
    fileID: number;
    resourceInfo?: Map<number, ResourceInfo>;
    error?: string;
  }) {
    const pending = this.pendingEGFs.get(data.fileID);
    if (pending) {
      this.pendingEGFs.delete(data.fileID);
      if (data.error) {
        pending.reject(data.error);
      } else {
        pending.resolve(data.resourceInfo!);
      }
    }
  }

  private async fetchEGF(fileID: number): Promise<ArrayBuffer> {
    const revision = [17,18,21,23].includes(fileID) ? '?v=regional-quests-20261005'
      : [24, 25].includes(fileID) ? '?v=skills-20260920' : '';
    const url = worldResource(`/gfx/gfx${String(fileID).padStart(3, '0')}.egf${revision}`);

    const controller=new AbortController();
    this.downloads.set(fileID, controller);
    // Archive downloads can be large on mobile. Decoding has a separate timer;
    // do not expire its promise while bytes are still being downloaded.
    const timer=setTimeout(()=>controller.abort(),120000);
    try {
      const response = await fetch(url,{signal:controller.signal,cache:'no-cache'});
      if (!response.ok) throw new Error(`Failed to fetch ${url}: HTTP ${response.status}`);
      return await response.arrayBuffer();
    } finally {clearTimeout(timer);if(this.downloads.get(fileID)===controller)this.downloads.delete(fileID);}
  }

  async loadEGFs(fileIDs: number[]): Promise<void> {
    await Promise.all(fileIDs.map((id) => this.loadEGF(id)));
  }

  private scheduleBatchEGF(fileID: number): Promise<void> {
    if (this.egfs.has(fileID)) return Promise.resolve();

    if (this.batchPromises.has(fileID)) {
      return this.batchPromises.get(fileID)!.promise;
    }

    const pending = createPendingPromise<void>(0);
    this.batchPromises.set(fileID, pending);

    if (this.batchTimer === null) {
      this.batchTimer = setTimeout(() => {
        this.batchTimer = null;
        const entries = [...this.batchPromises.entries()];
        this.batchPromises.clear();
        Promise.all(
          entries.map(([id, p]) =>
            this.loadEGF(id).then(
              () => p.resolve(),
              (err) => p.reject(err),
            ),
          ),
        );
      }, 100);
    }

    return pending.promise;
  }

  async loadEGF(fileID: number): Promise<void> {
    this.ensureWorker();
    const generation = this.workerGeneration;
    if (this.egfs.has(fileID)) return;
    if (this.pendingEGFs.has(fileID)) {
      await this.pendingEGFs.get(fileID)?.promise;
      return;
    }

    const pending = createPendingPromise<Map<number, ResourceInfo>>(0);
    this.pendingEGFs.set(fileID, pending);
    let decodeTimeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const buffer = await this.fetchEGF(fileID);
      if (generation !== this.workerGeneration) throw new Error('Graphics worker was replaced; retry the download');
      decodeTimeout = setTimeout(() => {
        if (generation === this.workerGeneration) this.failWorker(new Error('Graphics archive decoding timed out'));
      }, 30000);
      this.worker.postMessage({ loadType: LoadType.EGF, buffer, fileID }, [buffer]);
      const resourceInfo = await pending.promise;
      this.egfs.set(fileID, resourceInfo);
    } catch(error) {
      pending.reject(error);
      throw error;
    } finally {
      clearTimeout(decodeTimeout);
      if(this.pendingEGFs.get(fileID)===pending)this.pendingEGFs.delete(fileID);
    }
  }

  loadResource(fileID: number, resourceID: number): Promise<ImageBitmap> {
    const key = `${fileID}:${resourceID}`;
    const existing = this.bitmapRequests.get(key);
    if (existing) return existing;
    const request = this.loadResourceImpl(fileID, resourceID).finally(() => {
      if (this.bitmapRequests.get(key) === request) this.bitmapRequests.delete(key);
    });
    this.bitmapRequests.set(key, request);
    return request;
  }

  async preloadCachedResource(fileID: number, resourceID: number): Promise<void> {
    // Do not start another large archive download or decode a missing resource
    // speculatively. Real destination preparation remains authoritative.
    if (this.workerFailed || !this.egfs.get(fileID)?.has(resourceID)) return;
    await this.loadResource(fileID, resourceID);
  }

  private async loadResourceImpl(fileID: number, resourceID: number): Promise<ImageBitmap> {
    this.ensureWorker();
    const cacheKey = `${fileID}:${resourceID}`;

    // Tier 2: in-memory LRU
    if (this.bitmapCache.has(cacheKey)) {
      // Move to end (most recently used)
      const bm = this.bitmapCache.get(cacheKey)!;
      this.bitmapCache.delete(cacheKey);
      this.bitmapCache.set(cacheKey, bm);
      return bm;
    }

    if (!this.egfs.has(fileID)) {
      await this.scheduleBatchEGF(fileID);
    }

    if (this.pendingResources.has(cacheKey)) {
      const result = await this.pendingResources.get(cacheKey)!.promise;
      return this.toImageBitmap(cacheKey, result);
    }

    const pending = createPendingPromise<{
      pixels: ArrayBuffer;
      width: number;
      height: number;
    }>(30000, error => this.failWorker(error));
    this.pendingResources.set(cacheKey, pending);

    try {
      this.worker.postMessage({loadType:LoadType.DIB,fileID,resourceID});
      const result=await pending.promise;
      return await this.toImageBitmap(cacheKey,result);
    } catch(error) {pending.reject(error);throw error;}
    finally {if(this.pendingResources.get(cacheKey)===pending)this.pendingResources.delete(cacheKey); }
  }

  private async toImageBitmap(
    cacheKey: string,
    result: { pixels: ArrayBuffer; width: number; height: number },
  ): Promise<ImageBitmap> {
    const pixels = new Uint8ClampedArray(result.pixels);
    const imageData = new ImageData(pixels, result.width, result.height);
    const bm = await createImageBitmap(imageData);

    this.bitmapCache.set(cacheKey, bm);
    this.pixelCache.set(cacheKey, {
      pixels,
      width: result.width,
      height: result.height,
    });
    this.trimCache();
    return bm;
  }

  getRawPixels(
    fileID: number,
    resourceID: number,
  ): { pixels: Uint8ClampedArray; width: number; height: number } | undefined {
    return this.pixelCache.get(`${fileID}:${resourceID}`);
  }

  destroy() {
    this.workerGeneration++;
    for (const controller of this.downloads.values()) controller.abort();
    this.downloads.clear();
    if (this.batchTimer !== null) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    this.worker.terminate();
    for (const bm of this.bitmapCache.values()) {
      bm.close();
    }
    this.bitmapCache.clear();
    this.pixelCache.clear();
    this.egfs.clear();
    for(const pending of this.pendingEGFs.values())pending.reject(new Error('Graphics loader closed'));
    for(const pending of this.pendingResources.values())pending.reject(new Error('Graphics loader closed'));
    for(const pending of this.batchPromises.values())pending.reject(new Error('Graphics loader closed'));
    this.batchPromises.clear();
    this.pendingEGFs.clear();
    this.pendingResources.clear();
  }
}
